import { Link } from "react-router-dom";
import { Radio } from "lucide-react";
import BlogFormulaCard from "../../components/BlogFormula";

/*
 * Blog article: "Your Testosterone Level, Right Now"
 * Rendered inside BlogPost.jsx via <article className="legal-prose">.
 */

const MODEL_LATEX = String.raw`\begin{gathered}
\text{Live Testosterone Readout:} \\[0.4em]
T(t) = \underbrace{\frac{7\,T_{\mathrm{draw}}}{\mathcal{E}(D_0)}\,\frac{\langle C_{ss}\rangle_W}{C_{ss}(t_{\mathrm{draw}})}}_{\text{your baseline}}
       \sum_{i\,:\,t_i\le t}\underbrace{\mathcal{E}_i}_{\text{logged dose}} \\[0.4em]
\times\,\underbrace{\frac{k_{e,i}\,k_{a,i}}{k_{a,i}-k_{e,i}}
       \left(e^{-k_{e,i}(t-t_i)}-e^{-k_{a,i}(t-t_i)}\right)}_{\text{absorption + elimination}} \\[1.4em]
\text{Dose Times:} \\[0.3em]
t_i \in \underbrace{\{\text{Protocol Log}\}\cup\{t_{\mathrm{tick}}\}}_{\text{today counts once taken}} \\[1.4em]
\text{Steady-State Superposition (your baseline):} \\[0.3em]
C_{ss}(t) = \sum_{j=1}^{n} R_j(t-t_j), \quad
\langle C_{ss}\rangle_W = \frac{1}{|W|}\int_W C_{ss}(t)\,dt \\[1.4em]
\text{Delivered Testosterone (per route):} \\[0.3em]
\mathcal{E}(D) = \begin{cases} D\,\varepsilon & \text{injectable}\\[4pt]
  \dfrac{V_{max}\,D}{K_m+D} & \text{transdermal (saturating)} \end{cases}
\qquad \mathcal{E}_i = \frac{\mathcal{E}(f_i\,d_i)}{f_i} \\[1.4em]
\text{Absorption \& Elimination:} \\[0.3em]
k_{a,i}: \; \left(\operatorname*{arg\,max}_{t\,\in\,W}\,\sum_j A_i(t-t_j)\right) - t_{j^{*}} = \Delta t_{\mathrm{peak}} \\[0.4em]
k_{e,i} = \frac{\ln 2}{t_{1/2,i}}, \quad
t_{\mathrm{draw}} \in \Big\{\text{trough},\;\text{peak},\;\underbrace{t_{\mathrm{app}}+2.5\,\mathrm{h}}_{\text{cream peak}}\Big\} \\[1.2em]
{\scriptstyle \text{Parameters:}} \\[0.2em]
{\scriptstyle d_i = \text{logged dose}, \quad f_i = \text{doses per week}, \quad D_0 = \text{weekly dose at your blood test}} \\[0.3em]
{\scriptstyle \varepsilon = \text{ester fraction}, \quad t_i = \text{logged dose times}, \quad W = \text{steady-state window}} \\[0.3em]
{\scriptstyle A_i = \text{single-dose response}, \quad t_{j^{*}} = \text{dose before the peak}, \quad t_{1/2} = \text{half-life}} \\[0.3em]
{\scriptstyle R_j = \text{route-specific release}, \quad V_{max},\,K_m = \text{site saturation}, \quad t_{\mathrm{app}} = \text{cream application time}}
\end{gathered}`;

export default function ArticleBody() {
    return (
        <>
            <h2>Quick answer</h2>
            <p>
                The Live Testosterone Readout on My Protocol is an estimate
                of your Total T right now. It climbs after each dose you
                log, drifts down toward trough before the next one, and is
                calibrated to your own blood test and the protocol that
                produced it, not a population average.
            </p>

            <h2>What it lets you see</h2>
            <p>
                A blood test gives you one point on a curve that moves
                every hour of the week. The readout fills in the rest of
                that curve, which answers questions a single draw can't:
            </p>
            <ul>
                <li>
                    <strong>How high your level climbs</strong> after a
                    dose, and how far it drifts before the next one.
                </li>
                <li>
                    <strong>Where you are when you feel your best.</strong>{" "}
                    If energy, mood, or libido dip on certain days, you
                    can check whether those days line up with the low
                    point between doses.
                </li>
                <li>
                    <strong>What a frequency change does.</strong> Split
                    the same weekly dose into more frequent injections
                    and the range tightens. Spread it out and the range
                    widens.
                </li>
                <li>
                    <strong>When your level actually peaks</strong> after
                    an injection or application.
                </li>
            </ul>
            <p>
                Answering any of these with bloodwork would take a series
                of draws at different points in your dosing schedule. The
                readout estimates them from one blood test, your Protocol
                Log and published pharmacokinetics.
            </p>

            <h2>Why it starts from your blood test</h2>
            <p>
                Men on the same dose can end up at very different levels,
                because some respond more strongly to a dose than others.
                Your{" "}
                <Link to="/blog/understanding-dose-response">
                    Responder Type
                </Link>{" "}
                describes where you sit.
            </p>
            <p>
                TRT Plus does not use a population average to estimate
                your level. That would be like My Fitness Pal giving you
                a population average weight, landing you at 200 lbs and
                30% body fat no matter what you ate.
            </p>
            <p>
                Your readout starts from your own blood test. It knows
                where in your dosing schedule that blood was drawn and
                which protocol produced it, then runs forward on the
                doses you log.
            </p>

            <h2>How it works</h2>
            <p>
                <strong>The anchor.</strong> Your Total T result is one
                point on a curve that rises and falls between doses. It
                is first converted to the average level of the protocol
                that produced it, so a peak draw and a trough draw lead
                to the same place (
                <a
                    href="https://doi.org/10.1007/978-3-642-72185-4_11"
                    target="_blank"
                    rel="noopener noreferrer"
                >
                    Behre & Nieschlag, 1998
                </a>
                ;{" "}
                <a
                    href="https://doi.org/10.1111/andr.13108"
                    target="_blank"
                    rel="noopener noreferrer"
                >
                    Pastuszak et al., 2022
                </a>
                ). If your log shows a testosterone dose change shortly
                before the blood test, the baseline allows for levels
                that hadn't settled yet.
            </p>
            <p>
                <strong>
                    Delivered testosterone, not label milligrams.
                </strong>{" "}
                An ester adds weight the body has to cleave off, so 100
                mg of cypionate carries about 70 mg of testosterone and
                enanthate about 72 mg. For creams, the model uses what
                your application site actually absorbs, which levels off
                at higher doses (
                <a
                    href="https://doi.org/10.1210/jc.2018-00229"
                    target="_blank"
                    rel="noopener noreferrer"
                >
                    Bhasin et al., 2018
                </a>
                ).
            </p>
            <p>
                <strong>Every dose adds up.</strong> Your level today is
                the sum of every dose in your Protocol Log, each with its
                own fading tail. An earlier ester in your log keeps its
                own half-life, so a compound switch is handled dose by
                dose.
            </p>
            <p>
                <strong>It rises rather than jumps.</strong> A dose is
                absorbed over time rather than landing in the bloodstream
                at once, so each one climbs to a peak and then falls (
                <a
                    href="https://doi.org/10.1007/BF02353538"
                    target="_blank"
                    rel="noopener noreferrer"
                >
                    Garrett, 1994
                </a>
                ). The absorption rate is set so the readout peaks where
                the Levels chart draws its peak.
            </p>
            <p>
                <strong>Where you are now.</strong> Each dose from your
                Protocol Log is placed at the time it was logged. Marking
                today's dose as taken switches the readout from falling
                toward trough to climbing toward peak.
            </p>

            <BlogFormulaCard
                icon={Radio}
                eyebrow="Live Testosterone Readout"
                title="Live Testosterone Model"
                latex={MODEL_LATEX}
            />

            <h2>After a protocol change</h2>
            <p>
                The readout follows the doses in your Protocol Log.
                Change your compound, dose, or frequency and it climbs
                or eases toward the new level as you take each dose, the
                same way your blood does. The Live T explainer also shows
                where your current protocol settles and roughly by what
                date.
            </p>
            <p>
                How long that climb takes depends mainly on the
                compound, at about five half-lives:
            </p>

            <div className="not-prose my-8 rounded-xl border border-white/10 overflow-hidden">
                <table className="w-full text-sm text-left" data-testid="protocol-settles-table">
                    <thead className="bg-white/[0.03] text-white/60 uppercase tracking-wider text-[11px] font-mono">
                        <tr>
                            <th className="px-4 py-3">Compound</th>
                            <th className="px-4 py-3">Half-life</th>
                            <th className="px-4 py-3">Time to settle</th>
                        </tr>
                    </thead>
                    <tbody className="text-white/80">
                        <tr className="border-t border-white/5">
                            <td className="px-4 py-3">Cream / topical</td>
                            <td className="px-4 py-3 tabular-nums">0.5 days</td>
                            <td className="px-4 py-3 tabular-nums">3 days</td>
                        </tr>
                        <tr className="border-t border-white/5">
                            <td className="px-4 py-3">Propionate</td>
                            <td className="px-4 py-3 tabular-nums">1 day</td>
                            <td className="px-4 py-3 tabular-nums">5 days</td>
                        </tr>
                        <tr className="border-t border-white/5">
                            <td className="px-4 py-3">Enanthate</td>
                            <td className="px-4 py-3 tabular-nums">4.5 days</td>
                            <td className="px-4 py-3 tabular-nums">23 days (3.2 weeks)</td>
                        </tr>
                        <tr className="border-t border-white/5">
                            <td className="px-4 py-3">Cyp/Ent blend</td>
                            <td className="px-4 py-3 tabular-nums">6.25 days</td>
                            <td className="px-4 py-3 tabular-nums">31 days (4.5 weeks)</td>
                        </tr>
                        <tr className="border-t border-white/5">
                            <td className="px-4 py-3">Sustanon</td>
                            <td className="px-4 py-3 tabular-nums">7 days</td>
                            <td className="px-4 py-3 tabular-nums">35 days (5 weeks)</td>
                        </tr>
                        <tr className="border-t border-white/5">
                            <td className="px-4 py-3">Cypionate</td>
                            <td className="px-4 py-3 tabular-nums">8 days</td>
                            <td className="px-4 py-3 tabular-nums">40 days (5.7 weeks)</td>
                        </tr>
                        <tr className="border-t border-white/5">
                            <td className="px-4 py-3">Decanoate</td>
                            <td className="px-4 py-3 tabular-nums">10 days</td>
                            <td className="px-4 py-3 tabular-nums">50 days (7.1 weeks)</td>
                        </tr>
                        <tr className="border-t border-white/5">
                            <td className="px-4 py-3">Undecanoate (Nebido)</td>
                            <td className="px-4 py-3 tabular-nums">48 days</td>
                            <td className="px-4 py-3 tabular-nums">240 days (34.3 weeks)</td>
                        </tr>
                    </tbody>
                </table>
            </div>

            <h2>What it assumes</h2>
            <ul>
                <li>
                    The anchor is one blood test, so the readout is only
                    as current as that test.
                </li>
                <li>
                    Response is taken to scale with dose. That holds
                    across the TRT range and is weaker at
                    supraphysiological doses.
                </li>
                <li>
                    Half-lives are published averages from clinical
                    studies, so the shape of your levels between doses
                    is typical rather than measured. How high your level
                    sits comes from your own blood test.
                </li>
                <li>
                    Before your log starts, the readout assumes you were
                    on the first protocol you logged. From then on, a day
                    with no dose logged counts as no dose. Today's dose
                    is timed from when you mark it as taken.
                </li>
                <li>
                    It models blood testosterone. It does not measure
                    it.
                </li>
            </ul>

            <h2>Switching it on</h2>
            <p>
                Without a baseline, the readout shows a dash. The
                pulsing dot on the Testosterone Fluctuation chart still
                shows where you are between doses, but turning that into
                a level needs a blood test paired with the protocol that
                produced it.
            </p>
            <p>
                That pairing happens when you generate a{" "}
                <Link to="/blog/how-trt-plus-analyzes-your-bloodwork">
                    bloodwork report
                </Link>
                . The report pairs your result with what you were
                taking in the weeks leading up to the draw, from your
                Protocol Log or by asking you, and the readout switches
                on.
            </p>

            <p className="text-white/50 text-sm italic mt-10">
                The Live Testosterone Readout is an educational estimate,
                not medical advice, and it does not replace bloodwork.
                Consult with your healthcare provider before making
                medical decisions.
            </p>

            <h3>Related reading</h3>
            <ul>
                <li>
                    <Link to="/blog/understanding-dose-response">
                        Understanding Your Testosterone Dose Response
                    </Link>
                </li>
                <li>
                    <Link to="/blog/tracking-a-bad-protocol-is-still-a-bad-protocol">
                        Tracking a Bad Protocol Is Still a Bad Protocol
                    </Link>
                </li>
                <li>
                    <Link to="/blog/how-trt-plus-analyzes-your-bloodwork">
                        How TRT Plus Analyzes Your Bloodwork: A
                        Deterministic Engine, Then AI
                    </Link>
                </li>
            </ul>
        </>
    );
}
